import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ProductBarcode } from 'app/models/menu.model';
import { MenuOverrideService } from 'app/services/menu-override.service';

export interface ProductBarcodeRow {
  barcode: string;
  amount: number | null;
  containerName?: string;
}

// Таблица фасовок блюда: строки, совпадающие с данными iiko, помечаются источником «из iiko»,
// любая правка делает строку ручной. Оверрайд применяется на весь список целиком, поэтому
// «сбросить к данным iiko» возвращает все строки к originalBarcodes.
@Component({
  selector: 'app-product-barcodes',
  templateUrl: './product-barcodes.component.html',
  styleUrls: ['./product-barcodes.component.scss']
})
export class ProductBarcodesComponent {

  @Input() originalBarcodes: ProductBarcode[] | null;
  @Input() readonly = false;
  @Output() changed = new EventEmitter<void>();

  rows: ProductBarcodeRow[] = [];
  readonly columnsToDisplay = ['barcode', 'amount', 'source', 'actions'];

  private initialValue: ProductBarcode[] = [];

  @Input() set barcodes(value: ProductBarcode[] | null | undefined) {
    this.initialValue = (value ?? []).map(barcode => ({ ...barcode }));
    this.rows = (value ?? []).map(barcode => ({ ...barcode }));
  }

  // Фасовки, которые есть в iiko, но отсутствуют в текущем списке (по ключу штрихкода):
  // новые после выгрузки меню либо удалённые вручную. Оверрайд подменяет список целиком,
  // поэтому без подсказки админ не увидел бы новые фасовки, не сбросив ручные правки.
  get missingIikoBarcodes(): ProductBarcode[] {
    const currentBarcodes = new Set(this.rows.map(row => (row.barcode ?? '').trim()));
    return (this.originalBarcodes ?? []).filter(barcode => !currentBarcodes.has(barcode.barcode));
  }

  addMissing(barcode: ProductBarcode) {
    if (this.readonly) {
      return;
    }
    this.rows = [...this.rows, { ...barcode }];
    this.changed.emit();
  }

  get validationErrorKey(): string | null {
    if (this.rows.some(row => !(row.barcode ?? '').trim())) {
      return 'MENU.BARCODES.ERROR_BARCODE_REQUIRED';
    }
    const barcodes = this.rows.map(row => row.barcode.trim());
    if (new Set(barcodes).size !== barcodes.length) {
      return 'MENU.BARCODES.ERROR_BARCODE_UNIQUE';
    }
    if (this.rows.some(row => !(row.amount > 0))) {
      return 'MENU.BARCODES.ERROR_AMOUNT_POSITIVE';
    }
    return null;
  }

  isValid(): boolean {
    return this.validationErrorKey === null;
  }

  getValue(): ProductBarcode[] {
    return this.rows.map(row => {
      const barcode: ProductBarcode = { barcode: (row.barcode ?? '').trim(), amount: Number(row.amount) };
      if (row.containerName) {
        barcode.containerName = row.containerName;
      }
      return barcode;
    });
  }

  isDirty(): boolean {
    return !MenuOverrideService.areBarcodesEqual(this.initialValue, this.getValue());
  }

  isIikoRow(row: ProductBarcodeRow): boolean {
    return (this.originalBarcodes ?? [])
      .some(barcode => barcode.barcode === (row.barcode ?? '').trim() && barcode.amount === Number(row.amount));
  }

  // Строка с iiko-штрихкодом, но правленным количеством — её можно откатить по отдельности.
  isChangedIikoRow(row: ProductBarcodeRow): boolean {
    return !this.isIikoRow(row) && !!this.findIikoByBarcode(row);
  }

  // Добавленная вручную строка: такого штрихкода в iiko нет. Изменённые iiko-строки
  // помечаются только цветом, тег «вручную» — только у добавленных.
  isManualRow(row: ProductBarcodeRow): boolean {
    return !this.findIikoByBarcode(row);
  }

  getContainerName(row: ProductBarcodeRow): string {
    const iikoBarcode = this.findIikoByBarcode(row);
    return (iikoBarcode && iikoBarcode.containerName) || '';
  }

  getIikoAmount(row: ProductBarcodeRow): number | null {
    const iikoBarcode = this.findIikoByBarcode(row);
    return iikoBarcode ? iikoBarcode.amount : null;
  }

  restoreRow(index: number) {
    if (this.readonly) {
      return;
    }
    const iikoBarcode = this.findIikoByBarcode(this.rows[index]);
    if (!iikoBarcode) {
      return;
    }
    this.rows[index] = { ...iikoBarcode };
    this.rows = [...this.rows];
    this.changed.emit();
  }

  canRestore(): boolean {
    return !this.readonly && !MenuOverrideService.areBarcodesEqual(this.originalBarcodes ?? [], this.getValue());
  }

  restore() {
    this.rows = (this.originalBarcodes ?? []).map(barcode => ({ ...barcode }));
    this.changed.emit();
  }

  private findIikoByBarcode(row: ProductBarcodeRow): ProductBarcode | undefined {
    return (this.originalBarcodes ?? []).find(barcode => barcode.barcode === (row.barcode ?? '').trim());
  }

  onAddClick() {
    if (this.readonly) {
      return;
    }
    this.rows = [...this.rows, { barcode: '', amount: null }];
    this.changed.emit();
  }

  onDeleteClick(index: number) {
    this.rows = this.rows.filter((_, i) => i !== index);
    this.changed.emit();
  }

  onBarcodeChange(index: number, value: string) {
    this.rows[index].barcode = value;
    delete this.rows[index].containerName;
    this.changed.emit();
  }

  onAmountChange(index: number, value: string) {
    this.rows[index].amount = value === '' ? null : Number(value);
    delete this.rows[index].containerName;
    this.changed.emit();
  }
}
